import { useCallback, useMemo, useState } from 'react';

import {
  EmployeeIdentificationType,
  EmploymentBoardDecisionLockViewModel,
  EmploymentViewModel,
  FokParameterChoiceInput,
  UpdateEmploymentOldInput,
} from '@fora-monorepo/employer-my-pages-common/types';
import { trackCustomEvent } from '@fora-monorepo/webapp-analytics';
import {
  Animate,
  Assets,
  Button,
  ButtonTextOrIcon,
  Container,
  Feedback,
  Form,
  formatIDNumberOnChange,
  OptionType,
  useDatePickerInput,
  UseDatePickerInputArgs,
  useNumericInputField,
  useNumericInputFieldAsync,
  useRadioButton,
  useSelectSingle,
  useTextInputField,
} from '@foradevelopment/fora-ui';

import {
  validateCity,
  validateCoAddress,
  validateEmploymentEndDate,
  validateFirstName,
  validateLastName,
  validateMarkupStartDate,
  validatePostalCodeInternational,
  validatePostalCodeSweden,
  validateStreetAddress,
  validateStreetAddress2,
} from '../../common/utils';
import {
  AddressForm,
  EmploymentConfirmationDetailsSettingsOld,
  EmploymentFormStepWrapper,
  MarkupAndParametersForm,
  MarkupChangesLocked,
  PersonalIdentityRadioButtons,
  PersonalInformationForm,
  PersonDetails,
  TerminateEmploymentForm,
} from '../../components';
import {
  addOneDay,
  getCountriesOptions,
  getIDValidator,
  getMarkupStartDateOpenToDate,
  getParameterChoiceInput,
  hasParametersChanged,
  trackTraceMarkupStartDateValidation,
} from '../utils';
import { getExactDuplicateEmployments, updateEmploymentOld } from '../../redux/employment/employmentThunks';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getMarkupStartDateRangeMin } from '../utils/getMarkupStartDateRangeMin';
import { envConfig } from '../../config';
import { useTypedTranslation } from '../../i18n/i18n';

export const EditEmploymentForm = ({
  employment,
  boardDecisionLock,
  goToNextPanel,
}: {
  employment: EmploymentViewModel;
  boardDecisionLock: EmploymentBoardDecisionLockViewModel | null;
  goToNextPanel: (confirmationSettings: EmploymentConfirmationDetailsSettingsOld) => void;
}) => {
  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();
  const { faGroups, insuranceAgreementValidFromDate } = useAppSelector(state => state.employer.employerInfo);
  const countries = useAppSelector(state => state.foraGlobal.countries);
  const { monthlyReportingReleaseDate, payMonthMargin, ffFaGroupIdOnEmployment } = useAppSelector(
    state => state.foraGlobal.settings
  );

  const countriesOptions: OptionType[] = useMemo(() => getCountriesOptions(countries), [countries]);

  const [errorSubmittingMessage, setErrorSubmittingMessage] = useState('');
  const [submittingForm, setSubmittingForm] = useState(false);
  const [showNoChangesMadeMessage, setShowNoChangesMadeMessage] = useState(false);

  // Terminate employment
  const [terminateEmploymentChecked, setTerminateEmploymentChecked] = useState(!!employment.employmentEndDate);
  const employmentEndDateInputSettings = useMemo(
    () => ({
      initValue: employment.employmentEndDate || '',
      validate: validateEmploymentEndDate(employment.employmentStartDate),
      minDate: addOneDay(employment.employmentStartDate),
    }),
    [employment.employmentEndDate, employment.employmentStartDate]
  );
  const employmentEndDateInput = useDatePickerInput(employmentEndDateInputSettings);

  const duplicateValidator = useCallback(
    async (value: string) => {
      const result = await dispatch(getExactDuplicateEmployments({ employeeId: value }));
      if (getExactDuplicateEmployments.fulfilled.match(result)) {
        const exactDuplicateFoundResult = result.payload;
        if (exactDuplicateFoundResult) {
          return [t('errorMessages.exactDuplicateErrorText')];
        }
      }
      return [];
    },
    [dispatch, t]
  );

  const changeEmployeeIdRadioInput = useRadioButton('');

  // Personal details form (if unverified coordination number)
  const idValidator = useMemo(
    () => getIDValidator(changeEmployeeIdRadioInput.value || employment.employee.identificationType),
    [changeEmployeeIdRadioInput.value, employment.employee.identificationType]
  );
  const employeeIdInput = useNumericInputFieldAsync('', idValidator, duplicateValidator, formatIDNumberOnChange);

  const firstName = useTextInputField(employment.employee.firstName, validateFirstName);
  const lastName = useTextInputField(employment.employee.lastName, validateLastName);

  // AddressForm (if unverified coordination number)
  const initAddress = employment.employee.address;
  const countryInput = useSelectSingle(initAddress?.countryCode ?? 'SE');
  const coAddressInput = useTextInputField(initAddress?.coAddress ?? '', validateCoAddress);
  const streetAddressInput = useTextInputField(initAddress?.streetAddress ?? '', validateStreetAddress);
  const streetAddress2Input = useTextInputField(initAddress?.streetAddress2 ?? '', validateStreetAddress2);
  const postalCodeInputSwe = useNumericInputField(initAddress?.postalCode ?? '', validatePostalCodeSweden);
  const postalCodeInputIntl = useTextInputField(initAddress?.postalCode ?? '', validatePostalCodeInternational);
  const cityInput = useTextInputField(initAddress?.city ?? '', validateCity);

  // Markup
  const markupSelectInput = useSelectSingle();

  const datePickerInputSettings = useMemo((): UseDatePickerInputArgs => {
    const hasNewValidEmployeeId =
      !!changeEmployeeIdRadioInput.value && !employeeIdInput.props.errors.length && !!employeeIdInput.props.value;
    const employeeIdArg = hasNewValidEmployeeId ? employeeIdInput.props.value : employment.employee.employeeId;
    return {
      validate: validateMarkupStartDate(false, monthlyReportingReleaseDate, faGroups, employeeIdArg),
      minDate: getMarkupStartDateRangeMin(monthlyReportingReleaseDate, insuranceAgreementValidFromDate, employeeIdArg),
      openToDate: getMarkupStartDateOpenToDate(employment.activeMarkup, faGroups) ?? undefined,
    };
  }, [
    changeEmployeeIdRadioInput.value,
    employeeIdInput.props.errors.length,
    employeeIdInput.props.value,
    employment.activeMarkup,
    employment.employee.employeeId,
    faGroups,
    insuranceAgreementValidFromDate,
    monthlyReportingReleaseDate,
  ]);
  const markupStartDateInput = useDatePickerInput(datePickerInputSettings);

  // Parameter choices
  const [parameterChoicesInput, setParameterChoicesInput] = useState<
    { fokParameter: FokParameterChoiceInput; validateInput: () => boolean }[]
  >([]);

  const showNoChangesMadeNotification = async () => {
    setShowNoChangesMadeMessage(true);
    new Promise(resolve => setTimeout(resolve, 4000)).then(() => setShowNoChangesMadeMessage(false));
  };

  const isCoordinationNumber = employment.employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber;
  const isUnverifiedCoordinationNumber =
    employment.employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber &&
    !employment.employee.isVerified;

  const submitEditEmployment = async () => {
    setSubmittingForm(true);
    const errors: string[] = [];
    let changesMade = false;
    let formIsValid = true;
    let newMarkup = false;

    // Validate employee details
    if (isUnverifiedCoordinationNumber) {
      errors.push(...firstName.validateInputField(), ...lastName.validateInputField());
      changesMade =
        changesMade || [firstName.hasChangedSinceInit(), lastName.hasChangedSinceInit()].some(changeMade => changeMade);

      if (changeEmployeeIdRadioInput.value) {
        errors.push(...(await employeeIdInput.validateInputField()));
        changesMade = true;
      }

      if (changeEmployeeIdRadioInput.value !== EmployeeIdentificationType.PersonalIdentityNumber) {
        errors.push(
          ...coAddressInput.validateInputField(),
          ...streetAddressInput.validateInputField(),
          ...streetAddress2Input.validateInputField(),
          ...cityInput.validateInputField()
        );
        changesMade =
          changesMade ||
          [
            coAddressInput.hasChangedSinceInit(),
            streetAddressInput.hasChangedSinceInit(),
            streetAddress2Input.hasChangedSinceInit(),
            cityInput.hasChangedSinceInit(),
          ].some(changeMade => changeMade);
        if (countryInput.props.value === 'SE') {
          errors.push(...postalCodeInputSwe.validateInputField());
          changesMade = changesMade || postalCodeInputSwe.hasChangedSinceInit();
        } else {
          errors.push(...postalCodeInputIntl.validateInputField());
          changesMade = changesMade || postalCodeInputIntl.hasChangedSinceInit();
        }
      }
    }

    if (!boardDecisionLock) {
      // Validate new markup start date if not empty
      if (markupStartDateInput.value) {
        newMarkup = true;
        const markupStartDateValidationErrors = markupStartDateInput.validateDateInputValue();
        if (markupStartDateValidationErrors.length > 0) {
          trackTraceMarkupStartDateValidation(markupStartDateInput.value, markupStartDateValidationErrors);
        }
        errors.push(...markupStartDateValidationErrors);

        const markupSelectInvalid = !markupSelectInput.validate();
        if (markupSelectInvalid) errors.push('markupSelectInvalid');

        changesMade = true;
      }

      const parametersChanged = hasParametersChanged(parameterChoicesInput, employment.activeMarkup);
      if (parametersChanged) {
        const parametersInvalid = parameterChoicesInput.some(selectedParameter => !selectedParameter.validateInput());
        if (parametersInvalid) errors.push('parametersInvalid');

        changesMade = true;
      }
    }

    // Don't continue if no changes have been made
    if (!changesMade) {
      showNoChangesMadeNotification();
      setSubmittingForm(false);
      return;
    }
    formIsValid = errors.length === 0;

    if (formIsValid) {
      // Prepare markup input data
      let fokId: string;
      let startDate: string;
      if (newMarkup) {
        fokId = ffFaGroupIdOnEmployment
          ? (faGroups.find(faGroup => faGroup.faGroupId === markupSelectInput.props.value)?.fokId ?? '')
          : markupSelectInput.props.value;
        startDate = markupStartDateInput.value;
      } else if (employment.activeMarkup) {
        fokId = employment.activeMarkup.fokId;
        startDate = employment.activeMarkup.startDate;
      } else {
        // Unclear what case this is, when they don't have an active markup and haven't set a new one
        fokId = '';
        startDate = '';
      }

      const updateEmploymentInput: UpdateEmploymentOldInput = {
        employeeId: employeeIdInput.props.value || employment.employee.employeeId,
        identificationType:
          changeEmployeeIdRadioInput.value === EmployeeIdentificationType.PersonalIdentityNumber
            ? EmployeeIdentificationType.PersonalIdentityNumber
            : employment.employee.identificationType,
        partnerNumberIndividual: employment.employee.partnerNumberIndividual,
        firstName: firstName.props.value || employment.employee.firstName,
        lastName: lastName.props.value || employment.employee.lastName,
        addressInfo:
          // Note: The flag isUnverifiedCoordinationNumber should be used here, since the address comes from SPAR when verified
          // But due to validation in S4i, whe need to send an address down anyway (even though we don't display the address form)
          isCoordinationNumber && changeEmployeeIdRadioInput.value !== EmployeeIdentificationType.PersonalIdentityNumber
            ? {
                countryCode: countryInput.props.value || employment.employee.address.countryCode,
                coAddress: coAddressInput.hasChangedSinceInit()
                  ? coAddressInput.props.value
                  : employment.employee.address.coAddress,
                streetAddress: streetAddressInput.props.value || employment.employee.address.streetAddress,
                streetAddress2: streetAddress2Input.hasChangedSinceInit()
                  ? streetAddress2Input.props.value
                  : employment.employee.address.streetAddress2,
                postalCode:
                  countryInput.props.value === 'SE'
                    ? postalCodeInputSwe.props.value
                    : postalCodeInputIntl.props.value || employment.employee.address.postalCode,
                city: cityInput.props.value || employment.employee.address.city,
              }
            : undefined,
        markup: {
          fokId,
          startDate,
          fokParameterChoices: getParameterChoiceInput(parameterChoicesInput, employment.activeMarkup),
        },
      };
      setErrorSubmittingMessage('');
      const result = await dispatch(updateEmploymentOld({ unchangedEmployment: employment, updateEmploymentInput }));
      if (updateEmploymentOld.fulfilled.match(result)) {
        trackCustomEvent('Edited employment');
        goToNextPanel({
          employment: result.payload,
          countries,
          showAddress:
            changeEmployeeIdRadioInput.value === EmployeeIdentificationType.PersonalIdentityNumber
              ? false
              : isUnverifiedCoordinationNumber
                ? true
                : false,
          confirmationType: 'edit',
          showExistingDuplicateInfo: false,
        });
      }
      if (updateEmploymentOld.rejected.match(result)) {
        setErrorSubmittingMessage(
          envConfig.devLogging && result.payload && result.payload.message
            ? `Fel i backend: ${result.payload.message}`
            : t('errorMessages.contactSupport')
        );
        setSubmittingForm(false);
      }
    } else {
      setSubmittingForm(false);
    }
  };
  const submitTerminateEmployment = async () => {
    setSubmittingForm(true);
    let errors = [];
    // Validate terminate employment date
    if (terminateEmploymentChecked && employmentEndDateInput.value !== employment.employmentEndDate) {
      errors = [...employmentEndDateInput.validateDateInputValue()];
    } else {
      showNoChangesMadeNotification();
      return;
    }

    const formIsValid = errors.length === 0;
    if (formIsValid) {
      const updateEmploymentInput: UpdateEmploymentOldInput = {
        employeeId: employment.employee.employeeId,
        identificationType: employment.employee.identificationType,
        partnerNumberIndividual: employment.employee.partnerNumberIndividual,
        firstName: employment.employee.firstName,
        lastName: employment.employee.lastName,
        addressInfo: employment.employee.address,
        markup: {
          fokId: '',
          startDate: '',
          fokParameterChoices: [],
          // Terminating an employment means setting an end date on an empty markup object
          endDate: employmentEndDateInput.value,
        },
      };
      setErrorSubmittingMessage('');
      const result = await dispatch(updateEmploymentOld({ unchangedEmployment: employment, updateEmploymentInput }));
      if (updateEmploymentOld.fulfilled.match(result)) {
        trackCustomEvent('Terminated employment');
        goToNextPanel({
          employment: result.payload,
          showAddress: false,
          confirmationType: 'terminate',
          countries,
          showExistingDuplicateInfo: false,
        });
      }
      if (updateEmploymentOld.rejected.match(result)) {
        setErrorSubmittingMessage(
          envConfig.devLogging && result.payload && result.payload.message
            ? `Fel i backend: ${result.payload.message}`
            : t('errorMessages.contactSupport')
        );
        setSubmittingForm(false);
      }
    } else {
      setSubmittingForm(false);
    }
  };

  const deceasedAndUnverified = employment.employee.deathDate && !employment.employee.isVerified;
  return (
    <Form preventDefault>
      <Container marginBottom={40}>
        <PersonDetails
          firstName={employment.employee.firstName}
          lastName={employment.employee.lastName}
          identificationType={employment.employee.identificationType}
          employeeId={employment.employee.employeeId}
        />
      </Container>
      <EmploymentFormStepWrapper isFirstStep noPadding={terminateEmploymentChecked}>
        <TerminateEmploymentForm
          employmentEndDateInputProps={employmentEndDateInput.props}
          terminateEmployment={terminateEmploymentChecked}
          setTerminateEmployment={setTerminateEmploymentChecked}
          submitTerminateEmployment={submitTerminateEmployment}
          submitTerminateEmploymentLoading={submittingForm}
          initiallyTerminated={!!employment.employmentEndDate}
          payMonthMargin={payMonthMargin}
        />
      </EmploymentFormStepWrapper>
      {!terminateEmploymentChecked && (
        <>
          {isUnverifiedCoordinationNumber && (
            <>
              <EmploymentFormStepWrapper heading={t('form.editEmployee.personalInformationTitle')}>
                <PersonalIdentityRadioButtons
                  radioButtonProps={changeEmployeeIdRadioInput}
                  name={firstName.props.value || employment.employee.firstName}
                />
                <PersonalInformationForm
                  identificationType={(changeEmployeeIdRadioInput.value as EmployeeIdentificationType) || ''}
                  employeeIdInput={employeeIdInput.props}
                  firstNameInput={firstName.props}
                  lastNameInput={lastName.props}
                />
              </EmploymentFormStepWrapper>
              {changeEmployeeIdRadioInput.value !== EmployeeIdentificationType.PersonalIdentityNumber && (
                <EmploymentFormStepWrapper heading={t('form.editEmployee.addressInformationTitle')}>
                  <AddressForm
                    countryInput={countryInput.props}
                    coStreetAddressInput={coAddressInput.props}
                    streetAddressInput={streetAddressInput.props}
                    streetAddress2Input={streetAddress2Input.props}
                    postalCodeInput={
                      countryInput.props.value === 'SE' ? postalCodeInputSwe.props : postalCodeInputIntl.props
                    }
                    cityInput={cityInput.props}
                    isForeign={countryInput.props.value !== 'SE'}
                    countriesOptions={countriesOptions}
                  />
                </EmploymentFormStepWrapper>
              )}
            </>
          )}
          {!employment.employee.deathDate && (
            <EmploymentFormStepWrapper heading={t('form.editEmployee.markupTitle')}>
              {boardDecisionLock ? (
                <MarkupChangesLocked
                  activeMarkup={employment.activeMarkup}
                  boardDecisionLock={boardDecisionLock}
                  type="speechBubble"
                />
              ) : (
                <MarkupAndParametersForm
                  activeMarkup={employment.activeMarkup}
                  markupOptions={faGroups}
                  startDateInputProps={markupStartDateInput.props}
                  startDateInputValue={markupStartDateInput.value}
                  setParameterChoicesInput={setParameterChoicesInput}
                  markupSelectInput={markupSelectInput}
                  employmentStartDate={employment.employmentStartDate}
                  formType="edit"
                />
              )}
            </EmploymentFormStepWrapper>
          )}
        </>
      )}
      {errorSubmittingMessage && (
        <Feedback type="error" marginBottom={40} negativeSideMargins>
          {errorSubmittingMessage}
        </Feedback>
      )}
      {!terminateEmploymentChecked && (deceasedAndUnverified || !employment.employee.deathDate) ? (
        <Button type="button" onClick={submitEditEmployment} disabled={submittingForm}>
          <ButtonTextOrIcon
            icon={<Assets.Spinner center={false} width={20} color="white" />}
            textColor="white"
            isLoading={submittingForm}
          >
            {t('common.save')}
          </ButtonTextOrIcon>
        </Button>
      ) : null}
      <Animate.Height isTriggered={showNoChangesMadeMessage}>
        {showNoChangesMadeMessage && (
          <Feedback type="warning" size="slim" marginTop={20}>
            {t('form.editEmployee.noChangesMade')}
          </Feedback>
        )}
      </Animate.Height>
    </Form>
  );
};
